import { forwardRef } from 'react'
import { formatCurrency } from '../../lib/utils'

interface InvoiceItem {
  id: string
  product_name: string
  hsn_code?: string
  quantity: number
  unit_price: number
  gst_rate: number
  gst_type?: 'inclusive' | 'exclusive'
  cgst_amount: number
  sgst_amount: number
  igst_amount: number
  total_amount: number
}

interface InvoiceData {
  invoice_number: string
  invoice_date: string
  customer: {
    name: string
    phone?: string
    email?: string
    address?: string
    gst_number?: string
    state?: string
    state_code?: string
  }
  business: {
    name: string
    address: string
    gst_number: string
    phone: string
    email?: string
    state: string
    state_code: string
  }
  items: InvoiceItem[]
  subtotal: number
  total_cgst: number
  total_sgst: number
  total_igst: number
  total_gst: number
  grand_total: number
  payment_method: string
  round_off: number
}

interface GSTInvoiceProps {
  data: InvoiceData
}

export const GSTInvoice = forwardRef<HTMLDivElement, GSTInvoiceProps>(({ data }, ref) => {
  const isInterState = data.customer.state_code !== data.business.state_code
  
  return (
    <div ref={ref} className="bg-white p-8 max-w-4xl mx-auto text-black print:shadow-none">
      {/* Header */}
      <div className="border-b-2 border-gray-800 pb-4 mb-6">
        <div className="grid grid-cols-2 gap-8">
          <div>
            <h1 className="text-2xl font-bold text-gray-900 mb-2">{data.business.name}</h1>
            <p className="text-sm text-gray-600 whitespace-pre-line">{data.business.address}</p>
            <div className="mt-3 space-y-1 text-sm">
              <p><span className="font-semibold">Phone:</span> {data.business.phone}</p>
              {data.business.email && <p><span className="font-semibold">Email:</span> {data.business.email}</p>}
              <p><span className="font-semibold">GSTIN:</span> {data.business.gst_number}</p>
              <p><span className="font-semibold">State:</span> {data.business.state} ({data.business.state_code})</p>
            </div>
          </div>
          
          <div className="text-right">
            <h2 className="text-xl font-bold text-gray-900 mb-4">TAX INVOICE</h2>
            <div className="space-y-2 text-sm">
              <p><span className="font-semibold">Invoice No:</span> {data.invoice_number}</p>
              <p><span className="font-semibold">Date:</span> {data.invoice_date}</p>
              <p><span className="font-semibold">Payment Mode:</span> {data.payment_method.toUpperCase()}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Customer Details */}
      <div className="mb-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-3">Bill To:</h3>
        <div className="bg-gray-50 p-4 rounded-lg">
          <h4 className="font-semibold text-gray-900">{data.customer.name}</h4>
          {data.customer.address && <p className="text-sm text-gray-600 mt-1 whitespace-pre-line">{data.customer.address}</p>}
          <div className="mt-2 space-y-1 text-sm text-gray-600">
            {data.customer.phone && <p><span className="font-semibold">Phone:</span> {data.customer.phone}</p>}
            {data.customer.email && <p><span className="font-semibold">Email:</span> {data.customer.email}</p>}
            {data.customer.gst_number && <p><span className="font-semibold">GSTIN:</span> {data.customer.gst_number}</p>}
            {data.customer.state && <p><span className="font-semibold">State:</span> {data.customer.state} ({data.customer.state_code})</p>}
          </div>
        </div>
      </div>

      {/* Items Table */}
      <div className="mb-6">
        <table className="w-full border-collapse border border-gray-300">
          <thead className="bg-gray-100">
            <tr>
              <th className="border border-gray-300 px-3 py-2 text-left text-xs font-semibold text-gray-900 uppercase">
                S.No
              </th>
              <th className="border border-gray-300 px-3 py-2 text-left text-xs font-semibold text-gray-900 uppercase">
                Description
              </th>
              <th className="border border-gray-300 px-3 py-2 text-left text-xs font-semibold text-gray-900 uppercase">
                HSN/SAC
              </th>
              <th className="border border-gray-300 px-3 py-2 text-center text-xs font-semibold text-gray-900 uppercase">
                Qty
              </th>
              <th className="border border-gray-300 px-3 py-2 text-right text-xs font-semibold text-gray-900 uppercase">
                Unit Price
              </th>
              <th className="border border-gray-300 px-3 py-2 text-right text-xs font-semibold text-gray-900 uppercase">
                Taxable Value
              </th>
              <th className="border border-gray-300 px-3 py-2 text-center text-xs font-semibold text-gray-900 uppercase">
                GST Rate
              </th>
              {!isInterState ? (
                <>
                  <th className="border border-gray-300 px-3 py-2 text-right text-xs font-semibold text-gray-900 uppercase">
                    CGST
                  </th>
                  <th className="border border-gray-300 px-3 py-2 text-right text-xs font-semibold text-gray-900 uppercase">
                    SGST
                  </th>
                </>
              ) : (
                <th className="border border-gray-300 px-3 py-2 text-right text-xs font-semibold text-gray-900 uppercase">
                  IGST
                </th>
              )}
              <th className="border border-gray-300 px-3 py-2 text-right text-xs font-semibold text-gray-900 uppercase">
                Total Amount
              </th>
            </tr>
          </thead>
          <tbody>
            {data.items.map((item, index) => (
              <tr key={item.id} className="hover:bg-gray-50">
                <td className="border border-gray-300 px-3 py-2 text-sm">{index + 1}</td>
                <td className="border border-gray-300 px-3 py-2 text-sm font-medium">{item.product_name}</td>
                <td className="border border-gray-300 px-3 py-2 text-sm text-center">{item.hsn_code || '-'}</td>
                <td className="border border-gray-300 px-3 py-2 text-sm text-center">{item.quantity}</td>
                <td className="border border-gray-300 px-3 py-2 text-sm text-right font-mono">
                  {formatCurrency(item.unit_price)}
                </td>
                <td className="border border-gray-300 px-3 py-2 text-sm text-right font-mono">
                  {formatCurrency(item.unit_price * item.quantity)}
                </td>
                <td className="border border-gray-300 px-3 py-2 text-sm text-center">{item.gst_rate}%</td>
                {!isInterState ? (
                  <>
                    <td className="border border-gray-300 px-3 py-2 text-sm text-right font-mono">
                      {formatCurrency(item.cgst_amount)}
                    </td>
                    <td className="border border-gray-300 px-3 py-2 text-sm text-right font-mono">
                      {formatCurrency(item.sgst_amount)}
                    </td>
                  </>
                ) : (
                  <td className="border border-gray-300 px-3 py-2 text-sm text-right font-mono">
                    {formatCurrency(item.igst_amount)}
                  </td>
                )}
                <td className="border border-gray-300 px-3 py-2 text-sm text-right font-mono font-semibold">
                  {formatCurrency(item.total_amount)}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Summary */}
      <div className="grid grid-cols-2 gap-8">
        {/* GST Summary */}
        <div>
          <h3 className="text-lg font-semibold text-gray-900 mb-3">GST Summary</h3>
          <table className="w-full border-collapse border border-gray-300 text-sm">
            <thead className="bg-gray-100">
              <tr>
                <th className="border border-gray-300 px-3 py-2 text-left">GST Rate</th>
                <th className="border border-gray-300 px-3 py-2 text-right">Taxable Value</th>
                {!isInterState ? (
                  <>
                    <th className="border border-gray-300 px-3 py-2 text-right">CGST</th>
                    <th className="border border-gray-300 px-3 py-2 text-right">SGST</th>
                  </>
                ) : (
                  <th className="border border-gray-300 px-3 py-2 text-right">IGST</th>
                )}
                <th className="border border-gray-300 px-3 py-2 text-right">Total Tax</th>
              </tr>
            </thead>
            <tbody>
              {/* Group items by GST rate */}
              {Object.entries(
                data.items.reduce((acc, item) => {
                  const rate = item.gst_rate
                  if (!acc[rate]) {
                    acc[rate] = {
                      taxableValue: 0,
                      cgst: 0,
                      sgst: 0,
                      igst: 0
                    }
                  }
                  acc[rate].taxableValue += item.unit_price * item.quantity
                  acc[rate].cgst += item.cgst_amount
                  acc[rate].sgst += item.sgst_amount
                  acc[rate].igst += item.igst_amount
                  return acc
                }, {} as Record<number, { taxableValue: number, cgst: number, sgst: number, igst: number }>)
              ).map(([rate, summary]) => (
                <tr key={rate}>
                  <td className="border border-gray-300 px-3 py-2">{rate}%</td>
                  <td className="border border-gray-300 px-3 py-2 text-right font-mono">
                    {formatCurrency(summary.taxableValue)}
                  </td>
                  {!isInterState ? (
                    <>
                      <td className="border border-gray-300 px-3 py-2 text-right font-mono">
                        {formatCurrency(summary.cgst)}
                      </td>
                      <td className="border border-gray-300 px-3 py-2 text-right font-mono">
                        {formatCurrency(summary.sgst)}
                      </td>
                    </>
                  ) : (
                    <td className="border border-gray-300 px-3 py-2 text-right font-mono">
                      {formatCurrency(summary.igst)}
                    </td>
                  )}
                  <td className="border border-gray-300 px-3 py-2 text-right font-mono font-semibold">
                    {formatCurrency(isInterState ? summary.igst : summary.cgst + summary.sgst)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Total Summary */}
        <div>
          <h3 className="text-lg font-semibold text-gray-900 mb-3">Amount Summary</h3>
          <div className="space-y-3 text-sm">
            <div className="flex justify-between py-2 border-b border-gray-200">
              <span>Subtotal (Taxable Value):</span>
              <span className="font-mono font-semibold">{formatCurrency(data.subtotal)}</span>
            </div>
            
            {!isInterState ? (
              <>
                <div className="flex justify-between py-2 border-b border-gray-200">
                  <span>Total CGST:</span>
                  <span className="font-mono">{formatCurrency(data.total_cgst)}</span>
                </div>
                <div className="flex justify-between py-2 border-b border-gray-200">
                  <span>Total SGST:</span>
                  <span className="font-mono">{formatCurrency(data.total_sgst)}</span>
                </div>
              </>
            ) : (
              <div className="flex justify-between py-2 border-b border-gray-200">
                <span>Total IGST:</span>
                <span className="font-mono">{formatCurrency(data.total_igst)}</span>
              </div>
            )}
            
            <div className="flex justify-between py-2 border-b border-gray-200">
              <span>Total GST:</span>
              <span className="font-mono font-semibold">{formatCurrency(data.total_gst)}</span>
            </div>
            
            {data.round_off !== 0 && (
              <div className="flex justify-between py-2 border-b border-gray-200">
                <span>Round Off:</span>
                <span className="font-mono">{formatCurrency(data.round_off)}</span>
              </div>
            )}
            
            <div className="flex justify-between py-3 border-t-2 border-gray-800 text-lg font-bold">
              <span>Grand Total:</span>
              <span className="font-mono">{formatCurrency(data.grand_total)}</span>
            </div>
          </div>
        </div>
      </div>

      {/* Amount in Words */}
      <div className="mt-6 p-4 bg-gray-50 rounded-lg">
        <p className="text-sm">
          <span className="font-semibold">Amount in Words:</span>{' '}
          <span className="capitalize">{convertToWords(data.grand_total)} Only</span>
        </p>
      </div>

      {/* Terms and Footer */}
      <div className="mt-8 space-y-4 text-xs text-gray-600">
        <div>
          <h4 className="font-semibold text-gray-900 mb-2">Terms & Conditions:</h4>
          <ul className="space-y-1">
            <li>• Goods once sold will not be taken back or exchanged</li>
            <li>• All disputes are subject to local jurisdiction only</li>
            <li>• Payment is due within 30 days from the invoice date</li>
            <li>• This is a computer-generated invoice and does not require physical signature</li>
          </ul>
        </div>
        
        <div className="border-t border-gray-300 pt-4 text-center">
          <p>Thank you for your business!</p>
          <p className="mt-1 text-gray-500">
            Generated on {new Date().toLocaleString()} | Powered by digiBills
          </p>
        </div>
      </div>
    </div>
  )
})

GSTInvoice.displayName = 'GSTInvoice'

// Utility function to convert numbers to words (simplified version)
function convertToWords(amount: number): string {
  const ones = ['', 'One', 'Two', 'Three', 'Four', 'Five', 'Six', 'Seven', 'Eight', 'Nine']
  const tens = ['', '', 'Twenty', 'Thirty', 'Forty', 'Fifty', 'Sixty', 'Seventy', 'Eighty', 'Ninety']
  const teens = ['Ten', 'Eleven', 'Twelve', 'Thirteen', 'Fourteen', 'Fifteen', 'Sixteen', 'Seventeen', 'Eighteen', 'Nineteen']

  function convertHundreds(num: number): string {
    let result = ''
    
    if (num >= 100) {
      result += ones[Math.floor(num / 100)] + ' Hundred '
      num %= 100
    }
    
    if (num >= 20) {
      result += tens[Math.floor(num / 10)] + ' '
      num %= 10
    } else if (num >= 10) {
      result += teens[num - 10] + ' '
      return result
    }
    
    if (num > 0) {
      result += ones[num] + ' '
    }
    
    return result
  }

  if (amount === 0) return 'Zero'
  
  const crores = Math.floor(amount / 10000000)
  const lakhs = Math.floor((amount % 10000000) / 100000)
  const thousands = Math.floor((amount % 100000) / 1000)
  const hundreds = Math.floor(amount % 1000)
  const paise = Math.round((amount - Math.floor(amount)) * 100)

  let result = ''
  
  if (crores > 0) {
    result += convertHundreds(crores) + 'Crore '
  }
  
  if (lakhs > 0) {
    result += convertHundreds(lakhs) + 'Lakh '
  }
  
  if (thousands > 0) {
    result += convertHundreds(thousands) + 'Thousand '
  }
  
  if (hundreds > 0) {
    result += convertHundreds(hundreds)
  }
  
  if (Math.floor(amount) > 0) {
    result += 'Rupees '
  }
  
  if (paise > 0) {
    result += convertHundreds(paise) + 'Paise '
  }
  
  return result.trim()
}